import "./FileInput.scss";

import { ChangeEvent, ComponentProps, FC, MouseEvent, useRef, useState } from "react";

import Icon from "components/Icon/Icon";
import Text from "components/Text/Text";
import { useTranslation } from "modules";

import { forEach } from "lodash-es";

import FileList from "./FileList";

const FileInput: FC<IFileInputProps> = props => {
  const { accept = "image/*, .pdf", id, dataTestId, label, multiple, onChange } = props;

  // HOOKS
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  // LOCAL STATE
  const [files, setFiles] = useState<HTMLInputElement["files"]>(null);

  // LOCAL METHODS
  const handleOnChange = (event: TFileInputEvents) => {
    const changeEvent = {
      ...event,
      target: { ...event.target, id, value: inputRef.current?.value },
      files: inputRef.current?.files,
    } as FileInputChangeEvent;

    onChange(changeEvent);
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const uniqueFiles = getFiles(files);
    forEach(event.target.files, file => {
      const isDuplicate = uniqueFiles.some(
        selectedFile =>
          selectedFile.name === file.name &&
          selectedFile.size === file.size &&
          selectedFile.type === file.type
      );
      if (!isDuplicate) {
        uniqueFiles.push(file);
      }
    });
    setFileList(uniqueFiles);
    handleOnChange(event);
  };

  const handleFileRemove = (event: MouseEvent, fileName: File["name"]) => {
    const filteredFiles = getFiles(files).filter(file => file.name !== fileName);
    setFileList(filteredFiles);
    handleOnChange(event);
  };

  const getFiles = (fileList?: HTMLInputElement["files"]) => {
    return Array.from(fileList || []);
  };

  const setFileList = (files: FileList | File[]) => {
    const dataTransfer = new DataTransfer();
    forEach(files, file => {
      dataTransfer.items.add(file);
    });
    if (inputRef.current) {
      inputRef.current.files = dataTransfer.files;
      setFiles(dataTransfer.files);
    }
  };

  // RENDER
  return (
    <div className="file-input" data-testid={dataTestId}>
      {files && <FileList files={files} onRemove={handleFileRemove} />}

      <input
        accept={accept}
        className="file-input__input"
        id={id}
        multiple={multiple}
        onChange={handleFileSelect}
        ref={inputRef}
        type="file"
      />
      <label className="file-input__label" htmlFor={id}>
        <Icon src="DocumentUpload" />
        <Text tag="l5" text={label || t("input.file.placeholder", "Choose File")} />
      </label>
    </div>
  );
};

export default FileInput;

/* ------------------    TYPES    ------------------ */
interface IFileInputProps extends Partial<HTMLInputElement> {
  id: string;
  accept?: string;
  dataTestId?: string;
  label?: string;
  multiple?: boolean;
  onChange(event: FileInputChangeEvent): void;
}

type FileInputChangeEvent = TFileInputEvents & {
  files: HTMLInputElement["files"];
};

type TFileInputEvents = MouseEvent | ChangeEvent<HTMLInputElement>;

export type IFileInputComponentProps = ComponentProps<typeof FileInput>;
